import { DEFAULT_LOCALE, getNormalisedLocale, type VisibleLanguageOption } from 'i18n/util/locale'

export type LocaleRedirect = { locale: string | null; permanent: boolean }

const CANONICAL_LOCALE_SEGMENT = /^[a-z]{2}-(?:[A-Z][a-z]{3}-)?[A-Z]{2}$/
const UNDERSCORE_LOCALE_SEGMENT = /^[a-z]{2}_(?:[A-Z][a-z]{3}_)?[A-Z]{2}$/
const SHORT_LANGUAGE_SEGMENT = /^[a-z]{2}$/

function isLocaleLikeSegment(segment?: string): boolean {
  return !!segment && (
    CANONICAL_LOCALE_SEGMENT.test(segment) ||
    UNDERSCORE_LOCALE_SEGMENT.test(segment) ||
    SHORT_LANGUAGE_SEGMENT.test(segment)
  )
}

export default class UrlLocation {
  public readonly region?: string
  public readonly locale?: string

  constructor(
    public readonly path: string,
    public readonly query = '',
    public readonly location = '',
  ) {
    const segments = path.split('/')
    this.region = segments[1]
    this.locale = isLocaleLikeSegment(segments[2]) ? segments[2] : undefined
  }

  static fromLocation(
    location: { pathname: string; search: string; hash: string },
    redirect?: LocaleRedirect,
  ): UrlLocation {
    return new UrlLocation(location.pathname, location.search, location.hash).withRedirect(redirect)
  }

  static fromUrl(url: string, redirect?: LocaleRedirect): UrlLocation {
    const suffixIndex = url.search(/[?#]/)
    if (suffixIndex === -1) return new UrlLocation(url).withRedirect(redirect)

    const path = url.slice(0, suffixIndex)
    const suffix = url.slice(suffixIndex)
    const locationIndex = suffix.indexOf('#')

    return new UrlLocation(
      path,
      locationIndex === -1 ? suffix : suffix.slice(0, locationIndex),
      locationIndex === -1 ? '' : suffix.slice(locationIndex),
    ).withRedirect(redirect)
  }

  equals(other: UrlLocation): boolean {
    return this.path === other.path
  }

  getNormalisedLocale(languageOptions?: Array<VisibleLanguageOption>): string | null {
    return this.locale ? getNormalisedLocale(this.locale, languageOptions) : null
  }

  /**
   * Make clean URL match this target locale
   * i.e. /au/hotels + de-DE -> /au/de/hotels
   */
  getRedirectToLocale(
    targetLocale: string,
    languageOptions: Array<VisibleLanguageOption>,
  ): LocaleRedirect | null {
    const normalised = getNormalisedLocale(targetLocale, languageOptions)

    if (targetLocale === DEFAULT_LOCALE) return { locale: null, permanent: true }

    if (!this.locale && !normalised) return null
    if (this.locale === normalised) return null

    const prefix = this.locale?.substring(0, 2)
    // If the locale and normalised are different but the normalised matches the prefix
    if (prefix === normalised) return { locale: normalised, permanent: true }

    return { locale: normalised ?? null, permanent: false }
  }

  withoutLocale(): UrlLocation {
    if (!this.locale) return this

    const segments = this.path.split('/')
    return new UrlLocation(
      [...segments.slice(0, 2), ...segments.slice(3)].join('/'),
      this.query,
      this.location,
    )
  }

  withRedirect(redirect?: LocaleRedirect | null): UrlLocation {
    if (!redirect) return this

    const base = this.withoutLocale()
    if (!redirect.locale) return base

    const segments = base.path.split('/')
    return new UrlLocation(
      [segments[0], segments[1], redirect.locale, ...segments.slice(2)].join('/'),
      base.query,
      base.location,
    )
  }

  toString(): string {
    return `${this.path}${this.query}${this.location}`
  }
}
